import React, { useEffect, useState } from 'react';
import { Dialog, Typography, useMediaQuery } from '@material-ui/core';
import { useLocation } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { PhoneDisplay } from '@lib/common';
import { AlertBanner } from 'app/shared';
import Hero from 'app/assets/landing/hero.jpg';
import FacilitiesConstruction from 'app/assets/landing/facilities-construction.jpg';
import PlaceToRelax from 'app/assets/landing/place-to-relax.jpg';
import Food from 'app/assets/landing/food.jpg';
import LocationsMap from 'app/assets/landing/texas-nm-locations.svg';
import BrookfieldLogo from 'app/assets/landing/brookfield-logo.svg';
import BrookfieldDigital from 'app/assets/landing/brookfield-digital.jpg';
import BrookfieldEnergy from 'app/assets/landing/brookfield-energy.jpg';
import ArmadilloHotel from 'app/assets/landing/armadillo-hotel.jpg';

const InterpolatedPhoneDisplay = ({ children }: { children?: string[] }) => {
  return <PhoneDisplay phone={children?.[0]} />;
};

export const LandingContent: React.FC = ({ children }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { hash, key } = location;
  const isMobile = useMediaQuery('(max-width:960px)', { noSsr: true });

  // sheet state is local; it follows the URL only on mount / new navigation (deep link, header Book Now) so closing never navigates
  const [open, setOpen] = useState(false);
  useEffect(() => {
    setOpen(isMobile && new URLSearchParams(location.search).has('booking'));
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const close = () => setOpen(false);
  useEffect(() => {
    if (!isMobile) setOpen(false);
  }, [isMobile]);

  useEffect(() => {
    if (!hash) return;
    // ScrollToTop (earlier sibling under Router) has already run its scrollTo(0) in this flush; one frame of slack covers a later mount
    const frame = requestAnimationFrame(() =>
      document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth' })
    );
    return () => cancelAnimationFrame(frame);
  }, [hash, key]);

  const banner = t('home.banner', { defaultValue: null });

  const card = (
    <div className="booking-card">
      <div className="booking-card-header">
        <h2 className="booking-heading" id="booking-heading">
          Book a Room
        </h2>
        <p>Single rooms, crew blocks, and corporate stays.</p>
      </div>
      <div className="booking-form no-reset">{children}</div>
      <div className="booking-divider" aria-hidden="true" />
      <a className="corp-link" href="tel:18888886780">
        Corporate Sales 888-888-6780
      </a>
    </div>
  );

  return (
    <>
      {/* {!!banner && (
        <AlertBanner>
          <Typography display="inline">
            <strong>
              <Trans
                i18nKey="home.banner"
                components={{
                  phone: <InterpolatedPhoneDisplay />,
                }}
              />
            </strong>
          </Typography>
        </AlertBanner>
      )} */}
      <div className="guest-site" id="top">
        <section className="hero">
          <div className="hero-bg" aria-hidden="true">
            <img src={Hero} alt="" />
            <div className="hero-scrim" />
            <div className="hero-scrim-h" />
          </div>

          <div className="hero-content container">
            <div className="hero-text">
              <h1 className="hero-headline">
                <span className="small-headline">Performance starts with rest.</span>
                <br />
                <span className="ember">Stay Armadillo.</span>
              </h1>
              <p className="hero-sub" />
            </div>

            {isMobile ? (
              <Dialog
                open={open}
                keepMounted
                onClose={close}
                aria-labelledby="booking-heading"
                classes={{ paper: clsx('guest-site booking-sheet', open && 'open') }}
                BackdropProps={{ className: 'booking-sheet-backdrop' }}
              >
                <button
                  type="button"
                  className="booking-sheet-close"
                  aria-label="Close booking"
                  onClick={close}
                >
                  ×
                </button>
                {card}
              </Dialog>
            ) : (
              <aside className="hero-booking" id="booking" aria-labelledby="booking-heading">
                {card}
              </aside>
            )}
          </div>
        </section>

        <section className="armadillo-services section-pad" aria-labelledby="services-heading">
          <div className="container">
            <div className="services-intro" id="services">
              <div>
                <p className="section-eyebrow">
                  <span className="eyebrow-rule" /> Our services
                </p>
                <h2 id="services-heading">
                  More than a room.
                  <br />
                  <span className="italic ember">A complete turnkey housing solution.</span>
                </h2>
              </div>
              <p>
                From a room in our existing network to a custom-built facility, Armadillo brings
                housing, daily operations and meals together so your team can focus on the work
                ahead.
              </p>
            </div>
            <div className="services-panels">
              <article className="service-panel">
                <div className="service-photo">
                  <img
                    src={FacilitiesConstruction}
                    alt="Workforce housing facility under construction"
                    loading="lazy"
                    width="1024"
                    height="576"
                  />
                  <span>01</span>
                </div>
                <div className="service-panel-body">
                  <p className="service-category">Built around your project</p>
                  <h3>Facilities Construction</h3>
                  <p>
                    We develop workforce housing around the demands of your project—from location
                    and room count to layout and delivery schedule. Our team coordinates planning,
                    site development and construction to create a facility that works for your
                    workforce. Whether you need a new community or additional capacity, we bring
                    development expertise and modular housing solutions together to help you get
                    ready for the work ahead.
                  </p>
                </div>
              </article>
              <article className="service-panel">
                <div className="service-photo">
                  <img
                    src={PlaceToRelax}
                    alt="Comfortable guest accommodations"
                    loading="lazy"
                    width="900"
                    height="600"
                  />
                  <span>02</span>
                </div>
                <div className="service-panel-body">
                  <p className="service-category">A complete operating solution</p>
                  <h3>Operations and Management</h3>
                  <p>
                    We manage the details that make a workforce housing facility run—from
                    reservations and room assignments to housekeeping, maintenance and guest
                    support. Mobile keys and contact-free check-in simplify arrivals, while our
                    proprietary IT system enables detailed corporate reporting, giving your team
                    unrivaled visibility into stays and occupancy. We coordinate the day-to-day
                    operation so your crews have a dependable place to rest and your managers can
                    focus on the job.
                  </p>
                </div>
              </article>
              <article className="service-panel">
                <div className="service-photo">
                  <img
                    src={Food}
                    alt="Prepared meal service"
                    loading="lazy"
                    width="900"
                    height="600"
                  />
                  <span>03</span>
                </div>
                <div className="service-panel-body">
                  <p className="service-category">Meals that match the workday</p>
                  <h3>Meal Services</h3>
                  <p>
                    Keep your workforce fed without adding another stop to the day. Our meal
                    services bring hot meals and convenient grab-and-go options to the property,
                    with arrangements tailored to crew size and shift schedules. From early starts
                    to late returns, we work with your team to plan practical dining options and
                    corporate meal programs that support long stays and demanding workdays.
                  </p>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section className="locations" id="locations">
          <div className="locations-map-wrap" id="locations-map">
            <img
              src={LocationsMap}
              alt="Map of Armadillo locations in Texas and New Mexico, including Odessa, Orla, Pecos, Fort Stockton, Big Spring, Big Lake, Karnes City, Carrizo Springs, Jal, and Carlsbad."
            />
          </div>
          <div className="locations-overlay">
            <div className="container">
              <p className="section-eyebrow">
                <span className="eyebrow-rule" /> Locations
              </p>
              <h2 className="locations-headline">
                A complete network across TX and NM.
                <br />
                <span className="italic ember">Or wherever you need us.</span>
              </h2>
            </div>
          </div>
        </section>

        <section className="about-brookfield section-pad" aria-labelledby="about-heading">
          <div className="container">
            <div className="about-brookfield-top" id="about">
              <div className="about-brookfield-copy">
                <p className="section-eyebrow">
                  <span className="eyebrow-rule" /> About Armadillo
                </p>
                <h2 id="about-heading">
                  Built for your workforce.
                  <br />
                  <span className="italic ember">Backed by global scale.</span>
                </h2>
                <p>
                  Armadillo is a Brookfield subsidiary, bringing the scale, resources and operating
                  perspective of one of the world’s largest investment firms to workforce housing.
                </p>
                <p>
                  Our institutional commitment is to support the buildout of digital and energy
                  infrastructure—with dependable housing for the people making it happen.
                </p>
              </div>
              <aside className="brookfield-platform" aria-label="Brookfield global platform">
                <span className="brookfield-kicker">Part of the Brookfield family</span>
                <a
                  href="https://www.brookfield.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Visit Brookfield"
                >
                  <img
                    className="brookfield-logo"
                    src={BrookfieldLogo}
                    alt="Brookfield"
                    width="655"
                    height="100"
                  />
                </a>
                <p>One of the largest investors in AI and energy infrastructure.</p>
                <div className="brookfield-metrics">
                  <a
                    href="https://www.brookfield.com/about-us/businesses/asset-management"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <strong>$1T+</strong>
                    <span>Assets under management</span>
                  </a>
                  <a
                    href="https://www.brookfield.com/about-us/capabilities/infrastructure"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <strong>$262B</strong>
                    <span>Infrastructure AUM</span>
                  </a>
                  <a
                    href="https://www.brookfield.com/about-us/capabilities/energy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <strong>$144B</strong>
                    <span>Energy AUM</span>
                  </a>
                </div>
              </aside>
            </div>
            <div className="brookfield-sectors">
              <article className="brookfield-sector">
                <img
                  src={BrookfieldDigital}
                  alt="Data servers at Brookfield’s Data4 platform"
                  loading="lazy"
                  width="900"
                  height="600"
                />
                <div>
                  <p className="section-eyebrow">Digital infrastructure</p>
                  <h3>Supporting the next era of connectivity.</h3>
                  <p>
                    Brookfield is a leading investor in data and AI infrastructure, with a global
                    network of data centers, fiber and telecom towers. Armadillo supports the
                    workforce behind this expanding digital backbone.
                  </p>
                  <a
                    href="https://www.brookfield.com/about-us/capabilities/infrastructure"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Explore Brookfield infrastructure <span aria-hidden="true">↗</span>
                  </a>
                </div>
              </article>
              <article className="brookfield-sector">
                <img
                  src={BrookfieldEnergy}
                  alt="FirstEnergy electricity transmission infrastructure in Brookfield’s portfolio"
                  loading="lazy"
                  width="900"
                  height="600"
                />
                <div>
                  <p className="section-eyebrow">Energy infrastructure</p>
                  <h3>Helping power the buildout ahead.</h3>
                  <p>
                    Brookfield owns, operates and develops one of the world’s largest energy
                    infrastructure portfolios, alongside investments in utilities and midstream. We
                    are committed to housing the teams that build and maintain essential energy
                    systems.
                  </p>
                  <a
                    href="https://www.brookfield.com/about-us/capabilities/energy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Explore Brookfield energy <span aria-hidden="true">↗</span>
                  </a>
                </div>
              </article>
            </div>
            <div className="brookfield-insights" aria-label="Brookfield insights and video">
              <article className="brookfield-insight-article">
                <p className="section-eyebrow">Brookfield insights · White paper</p>
                <h3>Building the backbone of AI</h3>
                <p>
                  AI needs more than computing power. It needs data centers, energy and the physical
                  infrastructure to connect them. Explore Brookfield’s perspective on the investment
                  and buildout behind the next wave of AI.
                </p>
                <a
                  className="btn btn-secondary"
                  href="https://www.brookfield.com/views-news/insights/building-backbone-ai"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Read the insight <span aria-hidden="true">↗</span>
                </a>
              </article>
              <article className="brookfield-insight-video">
                <div className="brookfield-video-frame">
                  {/* spread: loading/referrerPolicy aren't in every @types/react 16.9 IframeHTMLAttributes */}
                  <iframe
                    src="https://www.youtube-nocookie.com/embed/Uxy4s2FP2EY"
                    title="Brookfield’s Bruce Flatt discusses the NVIDIA strategic partnership on CNBC"
                    allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    {...{
                      loading: 'lazy' as const,
                      referrerPolicy: 'strict-origin-when-cross-origin' as const,
                    }}
                  />
                </div>
                <div className="brookfield-video-caption">
                  <p className="section-eyebrow">Watch · Brookfield on CNBC</p>
                  <h3>Bruce Flatt on Brookfield’s NVIDIA partnership</h3>
                  <a href="https://youtu.be/Uxy4s2FP2EY" target="_blank" rel="noopener noreferrer">
                    Watch on YouTube <span aria-hidden="true">↗</span>
                  </a>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section className="outro-hero">
          <div className="outro-bg" aria-hidden="true">
            <img src={ArmadilloHotel} alt="" />
            <div className="outro-scrim" />
          </div>

          <div className="container outro-inner" id="outro">
            <p className="section-eyebrow">
              <span className="eyebrow-rule" /> Contact Us
            </p>
            <h2 className="outro-headline">
              <span className="ember">Let’s get the conversation started.</span>
            </h2>

            <div className="outro-ctas">
              <a href="mailto:info@ahghotel.com" className="btn btn-primary btn-lg">
                Email Us
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};
